import { Dialog as DialogPrimitive } from "@base-ui-components/react/dialog";
import { Menu as MenuPrimitive } from "@base-ui-components/react/menu";
import { Select as SelectPrimitive } from "@base-ui-components/react/select";
import { Tabs as TabsPrimitive } from "@base-ui-components/react/tabs";
import { useState, type ComponentProps, type ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "./lib/cn";

export namespace mui {
  function MenuRoot({ ...props }: ComponentProps<typeof MenuPrimitive.Root>) {
    return <MenuPrimitive.Root {...props} />;
  }
  function MenuTrigger({
    ...props
  }: ComponentProps<typeof MenuPrimitive.Trigger>) {
    return <MenuPrimitive.Trigger {...props} />;
  }
  function MenuPositioner({
    ...props
  }: ComponentProps<typeof MenuPrimitive.Positioner>) {
    return (
      <MenuPrimitive.Portal>
        <MenuPrimitive.Positioner {...props} />
      </MenuPrimitive.Portal>
    );
  }
  function MenuPopup({
    className,
    ...props
  }: ComponentProps<typeof MenuPrimitive.Popup>) {
    return (
      <MenuPrimitive.Popup
        {...props}
        className={(state) =>
          cn(
            "rounded-medium bg-surface-container text-on-surface shadow-elevation-light-level-2 max-w-70 min-w-28 origin-(--transform-origin) py-2 transition-[transform,scale,opacity] outline-none data-[ending-style]:scale-90 data-[ending-style]:opacity-0 data-[starting-style]:scale-90 data-[starting-style]:opacity-0",
            typeof className === "function" ? className(state) : className,
          )
        }
      />
    );
  }

  function MenuRadioGroup({
    ...props
  }: ComponentProps<typeof MenuPrimitive.RadioGroup>) {
    return <MenuPrimitive.RadioGroup {...props} />;
  }
  function MenuRadioItem({
    children,
    className,
    ...props
  }: ComponentProps<typeof MenuPrimitive.RadioItem>) {
    return (
      <MenuPrimitive.RadioItem
        {...props}
        className={(state) =>
          cn(
            "group text-body-lg data-checked:bg-secondary-container data-checked:text-on-secondary-container relative flex h-14 cursor-default items-center gap-3 px-2.5 outline-none select-none hover:cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-38",
            typeof className === "function" ? className(state) : className,
          )
        }
      >
        <span
          aria-hidden
          className="group-data-highlighted:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 group-data-checked:group-data-highlighted:bg-on-secondary-container/8 group-data-checked:group-active:text-on-secondary-container/10 absolute inset-0 transition"
        />
        {children}
      </MenuPrimitive.RadioItem>
    );
  }
  function MenuItem({
    children,
    className,
    ...props
  }: ComponentProps<typeof MenuPrimitive.Item>) {
    return (
      <MenuPrimitive.Item
        {...props}
        className={(state) =>
          cn(
            "group text-body-lg data-checked:bg-secondary-container data-checked:text-on-secondary-container relative flex h-14 cursor-default items-center gap-3 px-2.5 outline-none select-none hover:cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-38",
            typeof className === "function" ? className(state) : className,
          )
        }
      >
        <span
          aria-hidden
          className="group-data-highlighted:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 group-data-checked:group-data-highlighted:bg-on-secondary-container/8 group-data-checked:group-active:text-on-secondary-container/10 absolute inset-0 transition"
        />
        {children}
      </MenuPrimitive.Item>
    );
  }

  export const Menu = {
    Root: MenuRoot,
    Trigger: MenuTrigger,
    Positioner: MenuPositioner,
    Popup: MenuPopup,
    RadioGroup: MenuRadioGroup,
    RadioItem: MenuRadioItem,
    Item: MenuItem,
  };

  function DrawerRoot({
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Root>) {
    return <DrawerPrimitive.Root {...props} />;
  }

  function DrawerTrigger({
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Trigger>) {
    return <DrawerPrimitive.Trigger {...props} />;
  }

  function DrawerContent({
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Content>) {
    return <DrawerPrimitive.Content {...props} />;
  }

  function DrawerPortal({
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Portal>) {
    return <DrawerPrimitive.Portal {...props} />;
  }

  function DrawerTitle({
    className,
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Title>) {
    return <DrawerPrimitive.Title className="text-title-lg" {...props} />;
  }

  function DrawerDescription({
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Description>) {
    return <DrawerPrimitive.Description {...props} />;
  }

  function DrawerClose({
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Close>) {
    return <DrawerPrimitive.Close {...props} />;
  }

  function DrawerHandle({
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Handle>) {
    return <DrawerPrimitive.Handle {...props} />;
  }

  function DrawerOverlay({
    className,
    ...props
  }: ComponentProps<typeof DrawerPrimitive.Overlay>) {
    return (
      <DrawerPrimitive.Overlay
        className={cn("fixed inset-0 bg-black/36", className)}
        {...props}
      />
    );
  }

  export const Drawer = {
    Root: DrawerRoot,
    Trigger: DrawerTrigger,
    Content: DrawerContent,
    Portal: DrawerPortal,
    Title: DrawerTitle,
    Description: DrawerDescription,
    Close: DrawerClose,
    Handle: DrawerHandle,
    Overlay: DrawerOverlay,
  };

  function TabsRoot({ ...props }: ComponentProps<typeof TabsPrimitive.Root>) {
    return <TabsPrimitive.Root {...props} />;
  }

  function TabsList({
    className,
    ...props
  }: ComponentProps<typeof TabsPrimitive.List>) {
    return (
      <TabsPrimitive.List
        className={(state) =>
          cn(
            "border-b-outline-variant bg-surface-container-low z-elevation-level-0 relative flex overflow-x-auto border-b-2",
            typeof className === "function" ? className(state) : className,
          )
        }
        {...props}
      />
    );
  }

  function TabsTab({
    className,
    children,
    ...props
  }: ComponentProps<typeof TabsPrimitive.Tab>) {
    return (
      <TabsPrimitive.Tab
        className={(state) =>
          cn(
            "group text-title-sm aria-selected:text-primary text-on-surface-variant hover:text-on-surface relative grid grow place-items-center py-3.5 transition hover:cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-38",
            typeof className === "function" ? className(state) : className,
          )
        }
        {...props}
      >
        <span
          aria-hidden
          className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 transition"
        />
        {children}
      </TabsPrimitive.Tab>
    );
  }

  function TabsIndicator({
    className,
    ...props
  }: ComponentProps<typeof TabsPrimitive.Indicator>) {
    return (
      <TabsPrimitive.Indicator
        className={(state) =>
          cn(
            "bg-primary -z-elevation-level-1 absolute bottom-0 left-0 h-[3px] w-(--active-tab-width) translate-x-(--active-tab-left) rounded-t-full transition-all ease-in-out",
            typeof className === "function" ? className(state) : className,
          )
        }
        {...props}
      />
    );
  }

  function TabsPanel({ ...props }: ComponentProps<typeof TabsPrimitive.Panel>) {
    return <TabsPrimitive.Panel {...props} />;
  }

  export const Tabs = {
    Root: TabsRoot,
    List: TabsList,
    Tab: TabsTab,
    Indicator: TabsIndicator,
    Panel: TabsPanel,
  };

  export const Dialog = {
    Root: DialogPrimitive.Root,
    Trigger: DialogPrimitive.Trigger,
    Portal: DialogPrimitive.Portal,
    Close: DialogPrimitive.Close,
    Backdrop: function DialogBackdrop({
      className,
      ...props
    }: ComponentProps<typeof DialogPrimitive.Backdrop>) {
      return (
        <DialogPrimitive.Backdrop
          className={(state) =>
            cn(
              "fixed inset-0 bg-black/36 transition-all duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0",
              typeof className === "function" ? className(state) : className,
            )
          }
          {...props}
        />
      );
    },
    Popup: function DialogPopup({
      className,
      ...props
    }: ComponentProps<typeof DialogPrimitive.Popup>) {
      return (
        <DialogPrimitive.Popup
          className={(state) =>
            cn(
              "rounded-extra-large bg-surface-container-high text-on-surface",
              "shadow-elevation-light-level-3",
              "fixed top-1/2 left-1/2 -mt-8 -translate-x-1/2 -translate-y-1/2",
              "top-[calc(50%+1.25rem*var(--nested-dialogs))]",
              "w-96 p-6",
              "transition-all duration-150",
              "data-ending-style:scale-90 data-ending-style:opacity-0",
              "data-starting-style:scale-90 data-starting-style:opacity-0",
              typeof className === "function" ? className(state) : className,
            )
          }
          {...props}
        />
      );
    },
    Title: function DialogTitle({
      className,
      ...props
    }: ComponentProps<typeof DialogPrimitive.Title>) {
      return (
        <DialogPrimitive.Title
          className={(state) =>
            cn(
              "text-headline-sm",
              typeof className === "function" ? className(state) : className,
            )
          }
          {...props}
        />
      );
    },
    Description: function DialogDescription({
      className,
      ...props
    }: ComponentProps<typeof DialogPrimitive.Description>) {
      return (
        <DialogPrimitive.Description
          className={(state) =>
            cn(
              "text-body-md text-on-surface-variant text-pretty",
              typeof className === "function" ? className(state) : className,
            )
          }
          {...props}
        />
      );
    },
  };

  export const Select = {
    Root: SelectPrimitive.Root,
    Trigger: function SelectTrigger({
      variant = "surface-container",
      filled,
      className,
      ...props
    }: ComponentProps<typeof SelectPrimitive.Trigger> & {
      variant?: Variant;
      filled?: boolean;
    }) {
      return (
        <SelectPrimitive.Trigger
          {...props}
          className={(state) =>
            cn(
              "p-4",
              "flex justify-between",
              "text-body-lg",
              "border-b-outline border-b",
              backgroundColor(variant),
              onColor(filled ? invertVariant(variant) : variant),
              typeof className === "function" ? className(state) : className,
            )
          }
        />
      );
    },
    Value: SelectPrimitive.Value,
    Icon: SelectPrimitive.Icon,
    Portal: SelectPrimitive.Portal,
    Positioner: function SelectPositioner({
      className,
      ...props
    }: ComponentProps<typeof SelectPrimitive.Positioner>) {
      return (
        <SelectPrimitive.Positioner
          {...props}
          className={(state) =>
            cn(
              "outline-none",
              typeof className === "function" ? className(state) : className,
            )
          }
        />
      );
    },
    Popup: function SelectPopup({
      variant = "surface-container",
      className,
      ...props
    }: ComponentProps<typeof SelectPrimitive.Popup> & {
      variant?: Variant;
    }) {
      return (
        <SelectPrimitive.Popup
          {...props}
          className={(state) =>
            cn(
              "group shadow-elevation-light-level-2 origin-(--transform-origin) py-2",
              "outline-none",
              "rounded-medium",
              backgroundColor(variant),
              onColor(variant),
              typeof className === "function" ? className(state) : className,
            )
          }
        />
      );
    },
    Item: function SelectItem({
      children,
      className,
      ...props
    }: ComponentProps<typeof SelectPrimitive.Item>) {
      return (
        <SelectPrimitive.Item
          {...props}
          className={(state) =>
            cn(
              "group",
              "text-body-lg",
              "data-checked:bg-secondary-container data-checked:text-on-secondary-container",
              "relative flex h-14 min-w-(--anchor-width) cursor-default items-center gap-3 px-2.5",
              "outline-none select-none hover:cursor-pointer",
              "aria-disabled:pointer-events-none aria-disabled:opacity-38",
              typeof className === "function" ? className(state) : className,
            )
          }
        >
          <span
            aria-hidden
            className="group-data-highlighted:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 group-data-checked:group-data-highlighted:bg-on-secondary-container/8 group-data-checked:group-active:text-on-secondary-container/10 absolute inset-0 transition"
          />
          {children}
        </SelectPrimitive.Item>
      );
    },
    ItemText: SelectPrimitive.ItemText,
    ItemIndicator: SelectPrimitive.ItemIndicator,
  };

  export function FloatingActionButton({
    icon,
    className,
    ...props
  }: { icon: ReactNode } & ComponentProps<"button">) {
    return (
      <button
        className={cn(
          "group",
          "bg-tertiary-container text-on-tertiary-container outline-on-surface",
          "rounded-large relative grid place-items-center p-4 outline-offset-2 hover:cursor-pointer",
          "disabled:pointer-events-none disabled:opacity-38",
          className,
        )}
        {...props}
      >
        {icon}
        <span
          aria-hidden
          className="group-hover:bg-on-tertiary-container/8 group-active:bg-on-tertiary-container/10 rounded-large absolute inset-0 transition"
        />
      </button>
    );
  }
  export function NavigationRail() {}

  NavigationRail.Item = function NavigationRailItem({
    icon,
    label,
    active,
    disabled,
    ...props
  }: {
    icon: ReactNode;
    label: string;
    active?: boolean;
    disabled?: boolean;
  } & ComponentProps<"button">) {
    return (
      <button
        disabled={disabled}
        aria-disabled={disabled}
        data-active={active || undefined}
        className="group outline-on-surface rounded-large grid place-items-center gap-1 px-1 md:px-3 py-1 hover:cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-38"
        {...props}
      >
        <div
          className={`group-data-active:bg-secondary-container group-data-active:text-on-secondary-container relative grid place-items-center rounded-full px-4 py-1`}
        >
          {icon}
          <span
            aria-hidden
            className="group-data-active:group-hover:bg-on-secondary-container/8 group-data-active:group-active:bg-on-secondary-container/10 group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
          />
        </div>
        <div className="text-label-md group-data-active:text-on-surface text-on-surface-variant group-data-active:group-hover:text-on-surface hidden md:block">
          {label}
        </div>
      </button>
    );
  };

  export const buttonVariants = tv({
    slots: {
      base: "group text-label-lg relative cursor-pointer rounded-full px-6 py-2 text-center disabled:pointer-events-none disabled:opacity-38",
      state: "absolute inset-0 size-full rounded-full transition",
    },
    defaultVariants: {
      variant: "surface",
    },
    variants: {
      filled: { true: {} },
      variant: {
        surface: {
          base: "text-on-surface",
          state:
            "group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10",
        },
        primary: {
          base: "text-primary",
          state:
            "group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10",
        },
        secondary: {
          base: "text-secondary",
          state:
            "group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10",
        },
        tertiary: {
          base: "text-tertiary",
          state:
            "group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10",
        },
        "primary-container": {
          base: "text-on-primary-container",
          state:
            "group-hover:bg-on-primary-container/8 group-active:bg-on-primary-container/10",
        },
        "secondary-container": {
          base: "text-on-secondary-container",
          state:
            "group-hover:bg-on-secondary-container/8 group-active:bg-on-secondary-container/10",
        },
        "tertiary-container": {
          base: "text-on-tertiary-container",
          state:
            "group-hover:bg-on-tertiary-container/8 group-active:bg-on-tertiary-container/10",
        },
        "surface-container": {
          base: "text-on-surface-container",
          state:
            "group-hover:bg-on-surface-container-variant/8 group-active:bg-on-surface-container-variant/10",
        },
      },
    },
    compoundVariants: [
      {
        filled: true,
        variant: "surface",
        class: { base: "bg-surface text-on-surface", state: "" },
      },
      {
        filled: true,
        variant: "surface-container",
        class: {
          base: "bg-surface-container text-on-surface-container",
          state: "",
        },
      },
      {
        filled: true,
        variant: "primary",
        class: { base: "bg-primary text-on-primary", state: "" },
      },
      {
        filled: true,
        variant: "secondary",
        class: { base: "bg-secondary text-on-secondary", state: "" },
      },
      {
        filled: true,
        variant: "tertiary",
        class: { base: "bg-tertiary text-on-tertiary", state: "" },
      },
      {
        filled: true,
        variant: "primary-container",
        class: {
          base: "bg-primary-container text-on-primary-container",
          state: "",
        },
      },
      {
        filled: true,
        variant: "secondary-container",
        class: {
          base: "bg-secondary-container text-on-secondary-container",
          state: "",
        },
      },
      {
        filled: true,
        variant: "tertiary-container",
        class: {
          base: "bg-tertiary-container text-on-tertiary-container",
          state: "",
        },
      },
    ],
  });

  export function Link({
    variant,
    filled,
    className,
    children,
    ...props
  }: ComponentProps<"a"> & VariantProps<typeof buttonVariants>) {
    const { base, state } = buttonVariants({
      variant,
      filled,
    });

    return (
      <a className={base({ className })} {...props}>
        <span aria-hidden className={state({ className })} />
        {children}
      </a>
    );
  }

  export function Button({
    variant,
    filled,
    className,
    children,
    ...props
  }: ComponentProps<"button"> & VariantProps<typeof buttonVariants>) {
    const { base, state } = buttonVariants({
      variant,
      filled,
    });

    return (
      <button className={base({ className })} {...props}>
        <span aria-hidden className={state()} />
        {children}
      </button>
    );
  }

  export function FloatingLabel({
    variant = "surface",
    className,
    ...props
  }: ComponentProps<"label"> & {
    variant?:
      | "primary"
      | "secondary"
      | "tertiary"
      | "surface"
      | "primary-container"
      | "secondary-container"
      | "tertiary-container"
      | "surface-container";
  }) {
    return (
      <label
        {...props}
        className={cn(
          "px-4",
          "pointer-events-none absolute left-0 flex truncate font-normal transition-all select-none",
          "top-2 translate-y-0 peer-focus:top-2 peer-focus:translate-y-0",
          "peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2",
          "text-body-sm peer-focus:text-body-sm peer-disabled:text-body-sm peer-placeholder-shown:text-body-lg not-peer-placeholder-shown:text-body-sm",
          onColor(variant),
          "peer-invalid:text-error",
          className,
        )}
      />
    );
  }

  export type Variant =
    | "primary"
    | "secondary"
    | "tertiary"
    | "surface"
    | "primary-container"
    | "secondary-container"
    | "tertiary-container"
    | "surface-container"
    | `surface-container-${"lowest" | "low" | "high" | "highest"}`
    | "on-primary"
    | "on-secondary"
    | "on-tertiary"
    | "on-surface"
    | "on-surface-container"
    | "on-primary-container"
    | "on-secondary-container"
    | "on-tertiary-container"
    | "on-surface-variant"
    | `on-surface-container-${"lowest" | "low" | "high" | "highest"}`;

  export function backgroundColor(variant: Variant = "surface") {
    const map = {
      "bg-primary": variant === "primary",
      "bg-secondary": variant === "secondary",
      "bg-tertiary": variant === "tertiary",
      "bg-primary-container": variant === "primary-container",
      "bg-secondary-container": variant === "secondary-container",
      "bg-tertiary-container": variant === "tertiary-container",
      "bg-surface": variant === "surface",
      "bg-surface-container": variant === "surface-container",
      "bg-surface-container-lowest": variant === "surface-container-lowest",
      "bg-surface-container-low": variant === "surface-container-low",
      "bg-surface-container-high": variant === "surface-container-high",
      "bg-surface-container-highest": variant === "surface-container-highest",
      "bg-on-primary": variant === "on-primary",
      "bg-on-secondary": variant === "on-secondary",
      "bg-on-tertiary": variant === "on-tertiary",
      "bg-on-surface": variant === "on-surface",
      "bg-on-surface-container": variant === "on-surface-container",
      "bg-on-primary-container": variant === "on-primary-container",
      "bg-on-secondary-container": variant === "on-secondary-container",
      "bg-on-tertiary-container": variant === "on-tertiary-container",
      "bg-on-surface-variant": variant === "on-surface-variant",
      "bg-on-surface-container-lowest":
        variant === "on-surface-container-lowest",
      "bg-on-surface-container-low": variant === "on-surface-container-low",
      "bg-on-surface-container-high": variant === "on-surface-container-high",
      "bg-on-surface-container-highest":
        variant === "on-surface-container-highest",
    };

    return Object.keys(map).filter((key) => map[key as keyof typeof map]);
  }

  export function onColor(variant: Variant = "surface") {
    const map = {
      "text-primary": variant === "on-primary",
      "text-secondary": variant === "on-secondary",
      "text-tertiary": variant === "on-tertiary",
      "text-surface": variant === "on-surface",
      "text-surface-container": variant === "on-surface-container",
      "text-primary-container": variant === "on-primary-container",
      "text-secondary-container": variant === "on-secondary-container",
      "text-tertiary-container": variant === "on-tertiary-container",
      "text-on-primary": variant === "primary",
      "text-on-secondary": variant === "secondary",
      "text-on-tertiary": variant === "tertiary",
      "text-on-surface":
        variant === "surface" ||
        variant === "surface-container" ||
        variant === "surface-container-lowest" ||
        variant === "surface-container-low" ||
        variant === "surface-container-high" ||
        variant === "surface-container-highest",
      "text-on-primary-container": variant === "primary-container",
      "text-on-secondary-container": variant === "secondary-container",
      "text-on-tertiary-container": variant === "tertiary-container",
    };

    return Object.keys(map).filter((key) => map[key as keyof typeof map]);
  }

  export function invertVariant(variant: Variant) {
    if (variant.startsWith("on-")) {
      return variant.slice(3) as Variant;
    }
    return `on-${variant}` as Variant;
  }

  export function Input({
    variant = "surface",
    filled,
    className,
    ...props
  }: ComponentProps<"input"> & {
    variant?: Variant;
    filled?: boolean;
  }) {
    return (
      <input
        className={cn(
          "peer text-body-lg relative flex w-full",
          "px-4 pt-6 pb-2",
          "border-b-on-surface-variant border-b",
          "invalid:border-b-error",
          "transition-all placeholder:opacity-0 focus:outline-0 focus:placeholder:opacity-100 disabled:cursor-not-allowed disabled:opacity-38",
          filled && backgroundColor(variant),
          onColor(variant),
          className,
        )}
        {...props}
      />
    );
  }

  export function InputError({ className, ...props }: ComponentProps<"div">) {
    const [validationMessage, setValidationMessage] = useState<string>("");
    return (
      <div
        ref={(ref) => {
          if (ref === null) {
            return;
          }

          const input = ref.parentElement?.querySelector("input");
          if (input === null || input === undefined) {
            return;
          }

          function handleInput(this: HTMLInputElement, _e: Event) {
            setTimeout(() => {
              if (this.validationMessage !== "") {
                setValidationMessage(this.validationMessage);
              }
            }, 0);
          }

          input.addEventListener("input", handleInput);

          return () => {
            input.removeEventListener("input", handleInput);
          };
        }}
        className={cn(
          className,
          "text-body-sm text-error mt-1 hidden px-4 peer-invalid:block",
        )}
        {...props}
      >
        {validationMessage}
      </div>
    );
  }

  export function InputDescription({
    className,
    ...props
  }: ComponentProps<"div">) {
    return (
      <div className={cn("text-body-sm mt-1 px-4", className)} {...props} />
    );
  }
}
